import React from 'react';
import clsx from 'clsx';
import { MenuProps } from '@material-ui/core/Menu';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import { default as MUISelect, SelectProps as MUISelectProps } from '@material-ui/core/Select';

import './Select.css';
import InputLabel from 'components/input/InputLabel';

//
// --- Types ---

export interface CommonSelectProps extends Omit<MUISelectProps, 'labelId'> {
  className?: string;
  'data-testid'?: string;
  dropdownClassName?: string;
  formHelperContent?: React.ReactNode;
  labelClassName?: string;
}

// require a labelId if label is set
type SelectLabelProps = { label?: undefined; labelId?: never } | { label: string; labelId: string };
export type SelectProps = CommonSelectProps & SelectLabelProps;

//
// --- Select Component ---

const Select: React.FC<SelectProps> = props => {
  const {
    children,
    className,
    'data-testid': dataTestId = 'select-dropdown',
    disabled,
    dropdownClassName,
    error,
    fullWidth,
    formHelperContent,
    inputProps = {},
    label,
    labelClassName,
    /** this should be a unique value for each select component; only required if `label` is set */
    labelId,
    MenuProps = {},
    ...remainingSelectProps
  } = props;

  return (
    <FormControl
      className={clsx('select', className)}
      data-testid="select-form-control"
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
    >
      {label && (
        <InputLabel className={clsx('select-label', labelClassName)} id={labelId} shrink>
          {label}
        </InputLabel>
      )}
      <MUISelect
        className={clsx(['select-dropdown', dropdownClassName])}
        data-testid={dataTestId}
        fullWidth={fullWidth}
        inputProps={{
          'data-testid': 'select-hidden-input',
          ...inputProps
        }}
        labelId={label ? labelId : undefined}
        MenuProps={
          {
            'data-testid': 'select-menu',
            ...MenuProps
          } as MenuProps
        }
        {...remainingSelectProps}
      >
        {Array.isArray(children) ? React.Children.toArray(children) : children}
      </MUISelect>
      {formHelperContent && <FormHelperText>{formHelperContent}</FormHelperText>}
    </FormControl>
  );
};

export default Select;
